import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {
  DirectusService,
  DemonstrationAgenda,
  DirectusTranslation,
  ModuleFunction,
  UseCasePersona,
} from '@frontend/directus';
import {
  ProjectChapterType,
  ProjectStepType,
  DemonstrationControllerService,
  DemonstrationAgendaItemResponse,
  DemonstrationUseCaseItemRequest,
  DemonstrationUseCaseResponse,
  DemonstrationUseCaseScoreResponse,
  DemonstrationUseCaseUpsertRequest,
  ProjectRoleType,
} from '@frontend/shared-api';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {TooltipModule} from 'primeng/tooltip';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize, forkJoin} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {ProjectStepSaveButtonComponent} from '../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {CardModule} from 'primeng/card';
import {TabStatus, TabStatusEnum} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {logTabRender} from '../../../../../../shared/tabs/tab-render-log.util';
import {BadgeListComponent} from '../../../../../../shared/components/list/badge-list/badge-list.component';
import {BadgeListItem} from '../../../../../../shared/components/list/badge-list/badge-list.types';
import {AgendaTab, buildUseCaseTranslations, UseCaseItem} from '../components/demonstration.model';
import {DemonstrationUseCaseItemComponent} from '../components/demonstration-use-case-item/demonstration-use-case-item.component';
import {DemonstrationSummaryComponent} from '../components/demonstration-summary/demonstration-summary.component';
import {DemonstrationScoreLegendComponent} from '../components/demonstration-score-legend/demonstration-score-legend.component';
import {AccordionModule} from 'primeng/accordion';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {NoDataComponent} from '../../../../../../shared/components/no-data/no-data.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {GlobalSearchComponent} from '../../../../../../shared/components/search/global-search/global-search.component';
import {
  DropdownFilterComponent,
  DropdownFilterOption,
} from '../../../../../../shared/components/filter/dropdown-filter/dropdown-filter.component';
import {TranslocoDirective} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {SaveButtonTrackingService} from '../../../../../../shared/project-step-save-button/save-button-tracking.service';
import {UseCaseGroup} from './step-7-2-use-cases.types';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

const DEFAULT_INCLUDED = false;

@Component({
  selector: 'app-step-7-2-use-cases',
  imports: [
    FormsModule,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    TooltipModule,
    ProjectStepSaveButtonComponent,
    CardModule,
    BadgeListComponent,
    DemonstrationUseCaseItemComponent,
    DemonstrationSummaryComponent,
    DemonstrationScoreLegendComponent,
    AccordionModule,
    StepDotMenuComponent,
    NoDataComponent,
    LoadingComponent,
    GlobalSearchComponent,
    DropdownFilterComponent,
    TranslocoDirective,
    TranslateDirectusPipe,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-7-2-use-cases.component.html',
  styleUrl: './step-7-2-use-cases.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Step72UseCasesComponent extends ProjectBaseStepComponent {
  private readonly directusService = inject(DirectusService);
  private readonly demonstrationService = inject(DemonstrationControllerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastService);
  private readonly saveButtonTracking = inject(SaveButtonTrackingService);

  private readonly saveActions = viewChild<ElementRef<HTMLElement>>('saveActions');

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly directusAgendaItems = signal<DemonstrationAgenda[]>([]);
  protected readonly moduleFunctionsById = signal<Map<number, ModuleFunction>>(new Map());
  protected readonly personasById = signal<Map<string, UseCasePersona>>(new Map());
  protected readonly savedAgenda = signal<DemonstrationAgendaItemResponse[]>([]);
  protected readonly savedUseCases = signal<DemonstrationUseCaseResponse[]>([]);
  protected readonly tabs = signal<AgendaTab[]>([]);
  protected readonly activeTabIndex = signal(0);
  /** True while tab chrome has switched but heavy use-case content is not mounted yet. */
  protected readonly tabContentLoading = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly maxStrengthAttributes = signal(0);
  protected readonly maxFunctions = signal(0);
  private readonly inclusionOverrides = signal<Map<string, boolean | null>>(new Map());
  private readonly collapsedPanelIds = signal<Set<number>>(new Set());
  private readonly pendingSaveButtonReveal = signal(false);
  private tabSwitchToken = 0;
  private handledRevealRequests = 0;
  private readonly unsavedChangesTrackingId = crypto.randomUUID();

  protected readonly isStepPrerequisiteCompleted = computed<boolean>(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFD, stepId: ProjectStepType.RFD_DEMONSTRATION_AGENDA},
    ]);
  });

  protected readonly shouldShowContent = computed<boolean>(() => {
    return !this.loading() && this.isStepPrerequisiteCompleted() && this.tabs().length > 0;
  });

  protected readonly prerequisiteInfo = computed(
    (): {icon: string; titleKey: string; messageKey: string} | null => {
      if (this.loading()) {
        return null;
      }

      if (!this.isStepPrerequisiteCompleted()) {
        return {
          icon: 'pi pi-clock text-4xl text-gray-400 mb-4',
          titleKey: 'project.steps.previousStepIncompleteTitle',
          messageKey: 'project.steps.previousStepIncompleteMessage',
        };
      }

      return null;
    },
  );

  protected readonly currentTab = computed<AgendaTab | null>(() => {
    const tabs = this.tabs();
    const index = this.activeTabIndex();
    return tabs[index] ?? null;
  });

  protected readonly currentTabMaxStrengthAttributes = computed<number>(() => {
    const tab = this.currentTab();
    if (!tab) return 0;

    return tab.useCases.reduce(
      (max, useCase) => Math.max(max, useCase.score?.strengthAttributes ?? 0),
      0,
    );
  });

  protected readonly strengthAttributeOptions = computed<DropdownFilterOption<number>[]>(() => {
    const max = this.currentTabMaxStrengthAttributes();

    return Array.from({length: max}, (_, index) => {
      const count = max - index;
      return {label: `<= ${count}`, value: count};
    });
  });

  protected readonly currentTabMaxFunctions = computed<number>(() => {
    const tab = this.currentTab();
    if (!tab) return 0;

    return tab.useCases.reduce(
      (max, useCase) => Math.max(max, useCase.score?.attributesScored ?? 0),
      0,
    );
  });

  protected readonly functionCountOptions = computed<DropdownFilterOption<number>[]>(() => {
    const max = this.currentTabMaxFunctions();

    return Array.from({length: max}, (_, index) => {
      const count = max - index;
      return {label: `<= ${count}`, value: count};
    });
  });

  private readonly currentTabFilteredUseCases = computed<UseCaseItem[]>(() => {
    const tab = this.currentTab();
    if (!tab) return [];

    const term = this.searchTerm().trim().toLowerCase();
    const maxStrengthAttributes = this.maxStrengthAttributes();
    const maxFunctions = this.maxFunctions();

    return tab.useCases
      .filter(
        (useCase) =>
          !term ||
          useCase.translations.some((translation) =>
            translation.title.toLowerCase().includes(term),
          ),
      )
      .filter(
        (useCase) =>
          !maxStrengthAttributes ||
          (useCase.score?.strengthAttributes ?? 0) <= maxStrengthAttributes,
      )
      .filter((useCase) => !maxFunctions || (useCase.score?.attributesScored ?? 0) <= maxFunctions);
  });

  protected readonly currentTabGroups = computed<UseCaseGroup[]>(() => {
    const tab = this.currentTab();
    if (!tab) return [];

    const groups = new Map<
      number,
      {id: number; translations: DirectusTranslation[]; items: UseCaseItem[]}
    >();
    const noModuleFunctionItems: UseCaseItem[] = [];

    this.currentTabFilteredUseCases().forEach((uc) => {
      if (uc.moduleFunction) {
        if (!groups.has(uc.moduleFunction.id)) {
          groups.set(uc.moduleFunction.id, {
            id: uc.moduleFunction.id,
            translations: uc.moduleFunction.translations,
            items: [],
          });
        }
        groups.get(uc.moduleFunction.id)!.items.push(uc);
      } else {
        noModuleFunctionItems.push(uc);
      }
    });

    const result = Array.from(groups.values()).map((group) => ({
      ...group,
      badgeItems: this.mapUseCaseToBadgeItem(group.items, tab.abbreviation),
    }));

    if (noModuleFunctionItems.length > 0) {
      result.push({
        id: -1,
        translations: [],
        items: noModuleFunctionItems,
        badgeItems: this.mapUseCaseToBadgeItem(noModuleFunctionItems, tab.abbreviation),
      });
    }

    return result;
  });

  private readonly currentTabGroupIds = computed<number[]>(() => {
    return this.currentTabGroups().map((group) => group.id);
  });

  protected readonly openAccordionPanels = computed<number[]>(() => {
    const collapsedIds = this.collapsedPanelIds();
    return this.currentTabGroupIds().filter((id) => !collapsedIds.has(id));
  });

  private mapUseCaseToBadgeItem(useCases: UseCaseItem[], abbreviation?: string): BadgeListItem[] {
    return useCases.map((useCase) => ({
      key: useCase.generatedUseCaseId.toString(),
      identifier: abbreviation
        ? `${abbreviation} ${useCase.generatedUseCaseId}`
        : `UC ${useCase.generatedUseCaseId}`,
      translations: useCase.translations.map((t) => ({
        id: 0,
        relationship_id: '',
        language: t.language,
        name: t.title,
        description: t.description,
      })),
      originalItem: useCase,
      badgeTranslations: useCase.personaTranslations,
    }));
  }

  private inclusionKey(agendaId: number, generatedUseCaseId: number): string {
    return `${agendaId}:${generatedUseCaseId}`;
  }

  private resolveIncluded(agendaId: number, useCase: UseCaseItem): boolean {
    const overrides = this.inclusionOverrides();
    const key = this.inclusionKey(agendaId, useCase.generatedUseCaseId);
    const included = overrides.has(key) ? overrides.get(key) : useCase.included;

    return included ?? DEFAULT_INCLUDED;
  }

  private clearInclusionOverrides(tab: AgendaTab): void {
    this.inclusionOverrides.update((overrides) => {
      const remaining = new Map(overrides);
      tab.useCases.forEach((uc) =>
        remaining.delete(this.inclusionKey(tab.agendaId, uc.generatedUseCaseId)),
      );
      return remaining;
    });
  }

  protected includedOf(useCase: UseCaseItem): boolean {
    const tab = this.currentTab();

    if (!tab) {
      return useCase.included ?? DEFAULT_INCLUDED;
    }

    return this.resolveIncluded(tab.agendaId, useCase);
  }

  protected readonly currentTabTotalUseCases = computed<number>(() => {
    return this.currentTab()?.useCases.length ?? 0;
  });

  protected readonly currentTabIncludedUseCases = computed<number>(() => {
    const tab = this.currentTab();
    if (!tab) return 0;

    return tab.useCases.filter((uc) => this.resolveIncluded(tab.agendaId, uc) === true).length;
  });

  protected readonly currentTabPlannedDuration = computed<number>(() => {
    return this.currentTab()?.duration ?? 0;
  });

  protected readonly currentTabActualDuration = computed<number>(() => {
    const tab = this.currentTab();
    if (!tab) return 0;
    return tab.useCases
      .filter((uc) => this.resolveIncluded(tab.agendaId, uc) === true)
      .reduce((sum, uc) => sum + uc.duration, 0);
  });

  protected readonly currentTabDurationDifference = computed<number>(() => {
    return Math.abs(this.currentTabActualDuration() - this.currentTabPlannedDuration());
  });

  protected tabHasChanges(tab: AgendaTab): boolean {
    return tab.useCases.some((uc) => this.resolveIncluded(tab.agendaId, uc) !== uc.included);
  }

  protected readonly currentTabHasChanges = computed<boolean>(() => {
    const currentTab = this.currentTab();
    if (!currentTab) return false;

    return this.tabHasChanges(currentTab);
  });

  private readonly hasAnyUnsavedChanges = computed<boolean>(() => {
    return this.tabs().some((tab) => this.tabHasChanges(tab));
  });

  protected readonly canSave = computed<boolean>(
    () => this.currentTab() !== null && !this.hasDurationWarning(),
  );

  protected readonly hasDurationWarning = computed<boolean>(() => {
    const tab = this.currentTab();
    if (!tab) return false;

    const hasIncluded = tab.useCases.some((uc) => this.resolveIncluded(tab.agendaId, uc) === true);
    if (!hasIncluded) return false;

    const difference = this.currentTabActualDuration() - this.currentTabPlannedDuration();
    return difference > 5;
  });

  constructor() {
    super();

    effect(() => {
      if (!this.isStepPrerequisiteCompleted()) {
        this.loading.set(false);
        return;
      }

      if (this.directusAgendaItems().length === 0) {
        this.loadData();
      }
    });

    effect(() => {
      const directusItems = this.directusAgendaItems();
      const savedAgendaItems = this.savedAgenda();
      const savedUseCaseItems = this.savedUseCases();
      const moduleFunctionsById = this.moduleFunctionsById();
      const personasById = this.personasById();

      if (directusItems.length === 0 || savedAgendaItems.length === 0) return;

      const tabs = this.mergeTabs(
        directusItems,
        savedAgendaItems,
        savedUseCaseItems,
        moduleFunctionsById,
        personasById,
      );
      this.tabs.set(tabs);
    });

    effect(() => {
      const max = this.currentTabMaxStrengthAttributes();
      if (this.maxStrengthAttributes() === 0 || this.maxStrengthAttributes() > max) {
        this.maxStrengthAttributes.set(max);
      }
    });

    effect(() => {
      const max = this.currentTabMaxFunctions();
      if (this.maxFunctions() === 0 || this.maxFunctions() > max) {
        this.maxFunctions.set(max);
      }
    });

    effect(() => this.trackUnsavedChangesForStepLeave());

    effect(() => this.handleRevealRequest());

    effect(() => this.scrollToSaveButtonWhenPending());

    this.destroyRef.onDestroy(() =>
      this.saveButtonTracking.unregisterButton(this.unsavedChangesTrackingId),
    );
  }

  private trackUnsavedChangesForStepLeave(): void {
    if (this.hasAnyUnsavedChanges()) {
      this.saveButtonTracking.registerButton(this.unsavedChangesTrackingId);
    } else {
      this.saveButtonTracking.unregisterButton(this.unsavedChangesTrackingId);
    }
  }

  private handleRevealRequest(): void {
    const requests = this.saveButtonTracking.revealRequests();
    if (requests === this.handledRevealRequests) return;

    this.handledRevealRequests = requests;
    untracked(() => this.revealUnsavedChanges());
  }

  private revealUnsavedChanges(): void {
    const tabIndex = this.firstTabIndexWithChanges();
    if (tabIndex === -1) return;

    if (tabIndex !== this.activeTabIndex()) {
      this.onTabChange(tabIndex);
    }

    this.pendingSaveButtonReveal.set(true);
  }

  private firstTabIndexWithChanges(): number {
    const tabs = this.tabs();
    const activeIndex = this.activeTabIndex();

    if (tabs[activeIndex] && this.tabHasChanges(tabs[activeIndex])) {
      return activeIndex;
    }

    return tabs.findIndex((tab) => this.tabHasChanges(tab));
  }

  private scrollToSaveButtonWhenPending(): void {
    if (!this.pendingSaveButtonReveal()) return;

    const saveActions = this.saveActions();
    if (!saveActions) return;

    saveActions.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    this.pendingSaveButtonReveal.set(false);
  }

  private loadData(): void {
    this.loading.set(true);

    forkJoin({
      agenda: this.directusService.loadDemonstrationAgenda(),
      moduleFunctions: this.directusService.loadModuleFunctions(),
      personas: this.directusService.loadUseCasePersonas(),
      savedAgenda: this.demonstrationService.getAgenda({projectId: this.projectId}),
      savedUseCases: this.demonstrationService.getUseCases({projectId: this.projectId}),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: ({agenda, moduleFunctions, personas, savedAgenda, savedUseCases}) => {
          const moduleFunctionsById = this.mapModuleFunctionsById(moduleFunctions);
          const personasById = this.mapPersonasById(personas);

          this.directusAgendaItems.set(agenda);
          this.moduleFunctionsById.set(moduleFunctionsById);
          this.personasById.set(personasById);
          this.savedAgenda.set(savedAgenda);
          this.savedUseCases.set(savedUseCases);

          // Build tabs before loading ends so the empty-state does not flash.
          const tabs = this.mergeTabs(
            agenda,
            savedAgenda,
            savedUseCases,
            moduleFunctionsById,
            personasById,
          );
          this.tabs.set(tabs);
        },
        error: (error: unknown) => {
          console.error('Error loading demonstration use case data:', error);
          this.toast.showError('common.error', 'error.generic.detail');
          this.savedAgenda.set([]);
          this.savedUseCases.set([]);
          this.tabs.set([]);
        },
      });
  }

  private mapModuleFunctionsById(moduleFunctions: ModuleFunction[]): Map<number, ModuleFunction> {
    const byId = new Map<number, ModuleFunction>();
    for (const moduleFunction of moduleFunctions) {
      byId.set(moduleFunction.id, moduleFunction);
    }
    return byId;
  }

  private mapPersonasById(personas: UseCasePersona[]): Map<string, UseCasePersona> {
    const byId = new Map<string, UseCasePersona>();
    for (const persona of personas) {
      byId.set(persona.id, persona);
    }
    return byId;
  }

  private mergeTabs(
    directusItems: DemonstrationAgenda[],
    savedAgendaItems: DemonstrationAgendaItemResponse[],
    savedUseCaseItems: DemonstrationUseCaseResponse[],
    moduleFunctionsById: Map<number, ModuleFunction>,
    personasById: Map<string, UseCasePersona>,
  ): AgendaTab[] {
    const tabs: AgendaTab[] = [];

    const sortedAgendaItems = [...savedAgendaItems].sort(
      (a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0),
    );

    sortedAgendaItems.forEach((savedAgenda) => {
      const directusAgenda = directusItems.find(
        (d) => d.id === savedAgenda.demonstrationAgendaDirectusId,
      );

      if (!directusAgenda) {
        return;
      }

      const savedUseCasesForAgenda = savedUseCaseItems.filter(
        (s) => s.demonstrationAgendaItemId === savedAgenda.id,
      );

      if (savedUseCasesForAgenda.length === 0) {
        return;
      }

      const useCases: UseCaseItem[] = savedUseCasesForAgenda
        .map((savedUsecase) =>
          this.mapSavedUseCase(savedUsecase, moduleFunctionsById, personasById),
        )
        .sort((a, b) => this.compareUseCasesByImportance(a, b));

      if (useCases.length > 0) {
        tabs.push({
          agendaId: savedAgenda.id ?? 0,
          agendaDirectusId: savedAgenda.demonstrationAgendaDirectusId ?? 0,
          agendaTranslations: directusAgenda.translations,
          abbreviation: directusAgenda.abbreviation,
          duration: savedAgenda.duration ?? 0,
          orderNumber: savedAgenda.orderNumber ?? 0,
          useCases,
        });
      }
    });

    return tabs;
  }

  private mapSavedUseCase(
    savedUsecase: DemonstrationUseCaseResponse,
    moduleFunctionsById: Map<number, ModuleFunction>,
    personasById: Map<string, UseCasePersona>,
  ): UseCaseItem {
    const moduleFunctionId = savedUsecase.moduleFunctionId;
    const moduleFunction =
      moduleFunctionId != null ? moduleFunctionsById.get(moduleFunctionId) : undefined;
    const personaId = savedUsecase.personaId;

    return {
      generatedUseCaseId: savedUsecase.generatedUseCaseId ?? 0,
      translations: buildUseCaseTranslations(
        savedUsecase.titleDe ?? '',
        savedUsecase.descriptionDe ?? '',
        savedUsecase.titleEn,
        savedUsecase.descriptionEn,
      ),
      duration: savedUsecase.duration ?? 0,
      included: savedUsecase.included ?? null,
      sortOrder: savedUsecase.sortOrder ?? 0,
      score: savedUsecase.score,
      moduleFunction:
        moduleFunctionId != null
          ? {
              id: moduleFunctionId,
              translations: moduleFunction?.translations ?? [],
            }
          : undefined,
      personaId,
      personaTranslations: personaId ? personasById.get(personaId)?.translations : undefined,
    };
  }

  private compareUseCasesByImportance(a: UseCaseItem, b: UseCaseItem): number {
    const getImportance = (score: DemonstrationUseCaseScoreResponse | undefined) => {
      if (!score || !score.attributesScored) return null;
      return score.importance ?? null;
    };

    const importanceA = getImportance(a.score);
    const importanceB = getImportance(b.score);

    if (importanceA !== null && importanceB !== null) {
      if (importanceA !== importanceB) {
        return importanceB - importanceA;
      }
    }
    if (importanceA !== null && importanceB === null) return -1;
    if (importanceA === null && importanceB !== null) return 1;
    return a.sortOrder - b.sortOrder;
  }

  private calculateTabStatus(tab: AgendaTab): TabStatus {
    const totalUsecases = tab.useCases.length;
    const decidedUsecases = tab.useCases.filter((uc) => uc.included !== null).length;

    return {
      totalSections: totalUsecases,
      completedSections: decidedUsecases,
    };
  }

  private isTabFullyAnswered(tabStatus: TabStatus): boolean {
    return tabStatus.totalSections > 0 && tabStatus.completedSections === tabStatus.totalSections;
  }

  private isTabDurationOutOfTolerance(tab: AgendaTab): boolean {
    const planned = tab.duration ?? 0;
    const actual = tab.useCases
      .filter((uc) => this.resolveIncluded(tab.agendaId, uc) === true)
      .reduce((sum, uc) => sum + uc.duration, 0);

    return Math.abs(actual - planned) > 5;
  }

  protected onAccordionValueChange(
    value: string | number | string[] | number[] | null | undefined,
  ): void {
    const openIds = new Set<string | number>(Array.isArray(value) ? value : []);

    this.collapsedPanelIds.set(new Set(this.currentTabGroupIds().filter((id) => !openIds.has(id))));
  }

  protected onUseCaseIncludedChange(useCase: UseCaseItem, included: boolean | null): void {
    const currentTab = this.currentTab();
    if (!currentTab) return;

    const key = this.inclusionKey(currentTab.agendaId, useCase.generatedUseCaseId);

    this.inclusionOverrides.update((overrides) => new Map(overrides).set(key, included));
  }

  protected onSave(): void {
    if (this.saving() || !this.currentTabHasChanges() || !this.canSave()) return;

    this.saving.set(true);

    const currentTab = this.currentTab();
    if (!currentTab) {
      this.saving.set(false);
      return;
    }

    const items: DemonstrationUseCaseItemRequest[] = currentTab.useCases.map((uc) => {
      const included = this.resolveIncluded(currentTab.agendaId, uc);

      return {
        demonstrationAgendaItemId: currentTab.agendaId,
        generatedUseCaseId: uc.generatedUseCaseId,
        included,
      };
    });

    const request: DemonstrationUseCaseUpsertRequest = {items};

    this.demonstrationService
      .upsertUseCases({
        projectId: this.projectId,
        demonstrationUseCaseUpsertRequest: request,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: async (response: DemonstrationUseCaseResponse[]) => {
          // upsert returns all project use cases — replace local state instead of merging
          this.savedUseCases.set(response);
          this.clearInclusionOverrides(currentTab);

          await this.stepState.loadStepStatuses(this.projectId);
          await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
        },
        error: (error: unknown) => {
          console.error('Error saving useCases:', error);
          this.toast.showError('common.error', 'error.generic.detail');
        },
      });
  }

  protected onTabChange(value: string | number): void {
    const index = typeof value === 'string' ? parseInt(value, 10) : value;
    if (Number.isNaN(index) || index === this.activeTabIndex()) {
      return;
    }

    this.activeTabIndex.set(index);
    this.collapsedPanelIds.set(new Set());
    this.tabContentLoading.set(true);
    const token = ++this.tabSwitchToken;

    logTabRender('UC', index);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (token === this.tabSwitchToken) {
          this.tabContentLoading.set(false);
        }
      });
    });
  }

  protected getTabStatus(tab: AgendaTab): TabStatus | TabStatusEnum {
    const tabStatus = this.calculateTabStatus(tab);

    if (this.isTabFullyAnswered(tabStatus) && this.isTabDurationOutOfTolerance(tab)) {
      return TabStatusEnum.IN_PROGRESS;
    }

    return tabStatus;
  }

  protected readonly ProjectRoleType = ProjectRoleType;
  protected readonly ProjectStepType = ProjectStepType;
}
